import { useEffect, useMemo, useState } from 'react';
import CheckmarkIcon from '../assets/icons/CheckmarkIcon';
import CloseIcon from '../assets/icons/actions/CloseIcon';
import PhoneInput, { Country } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import useTheme from '../hooks/ui/useTheme';
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import classnames from 'classnames';
import CurrentLocationManager from '../services/api/CurrentLocationManager';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';

export interface PhoneNumberInputProps {
  placeholder: string;
  value: string;
  initialCountry?: Country;
  disabled?: boolean;
  validationErrors?: string[];
  className?: string;
  inputContainerStyle?: string;
  onChange: (value: string) => void;
  onChangeCountry?: (value: string) => void;
  id?: string;
}

export default function PhoneNumberInput(props: PhoneNumberInputProps) {
  const {
    value,
    initialCountry,
    placeholder,
    disabled,
    validationErrors,
    className,
    inputContainerStyle,
    onChange,
    onChangeCountry,
  } = props;

  const { company } = useSelector((state: StoreInterface) => state.initialData);

  const theme = useTheme();
  const [elementId, setElementId] = useState(0);
  const [country, setCountry] = useState<Country>(initialCountry);

  const isValid = useMemo(() => {
    if (!value) {
      return false;
    }

    return isValidPhoneNumber(value);
  }, [value]);

  const formattedValue = useMemo(() => {
    if (!value) {
      return '';
    }

    const parsed = parsePhoneNumber(value);

    if (!parsed) {
      return value;
    }

    return parsed.formatInternational().replace(' ', '');
  }, [value]);

  useEffect(() => {
    guessCountry();
  }, []);

  useEffect(() => {
    onChangeCountry && onChangeCountry(country);
  }, [country]);

  async function guessCountry() {
    if (country) {
      return;
    }

    const { success, response } = await CurrentLocationManager.get();

    if (!success) {
      return setCountry(company?.country ?? 'DE');
    }

    return setCountry(response.data.country_code);
  }

  function renderRemoveIcon() {
    return (
      <div className={removeIconContainer} onClick={() => onChange('')}>
        <CloseIcon className={iconStyle} color={theme.color.gray['500']} />
      </div>
    );
  }

  function renderCheckmarkIcon() {
    return (
      <div className={checkmarkIconContainer}>
        <CheckmarkIcon className={iconStyle} color={theme.color.white['100']} />
      </div>
    );
  }

  const onPaste = () => {
    setTimeout(() => setElementId(elementId + 1));
  };

  function changeValue(value: string) {
    return onChange && onChange(value ?? '');
  }

  function renderInput() {
    function renderActionIcons() {
      if (!value || value?.length === 0) {
        return null;
      }

      if (isValid) {
        return renderCheckmarkIcon();
      }

      return renderRemoveIcon();
    }

    return (
      <div
        className={classnames(
          inputContainer,
          {
            'border-danger': validationErrors && validationErrors.length > 0,
            'bg-gray': disabled,
          },
          inputContainerStyle,
        )}
      >
        <form onPaste={onPaste} onSubmit={(e) => e.preventDefault()} className={'w-full relative'}>
          {value?.length === 0 ? (
            <div className={placeholderContainer}>
              <p className="text-gray-700 text-sm">123 4567890</p>
            </div>
          ) : null}
          <PhoneInput
            data-test="phone-number-input"
            key={elementId}
            country={country ?? 'DE'}
            defaultCountry={country ?? 'DE'}
            placeholder={placeholder}
            value={formattedValue ?? ''}
            onChange={changeValue}
            onCountryChange={setCountry}
            international={true}
            className="w-full font-sm relative"
            countryCallingCodeEditable={false}
            disabled={disabled}
            onBlur={onPaste}
          />
        </form>
        {!disabled && <div className="ml-2">{renderActionIcons()}</div>}
      </div>
    );
  }

  return (
    <div className={classnames('w-full', className)}>
      {renderInput()}
      {validationErrors && (
        <div>
          {validationErrors.map((item) => (
            <p className="mt-xs text-danger" key={`validation-error-${item}`}>
              {item}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

const inputContainer =
  'placeholder:text-gray-700 text-black rounded flex flex-row p-sm items-center w-full border rounded-lg';

const placeholderContainer = 'absolute inset-0 pl-[85px] z-0';

const removeIconContainer = 'bg-gray-300 rounded-full flex justify-center items-center w-5 h-5';

const checkmarkIconContainer = 'bg-primary rounded-full flex justify-center items-center w-5 h-5';

const iconStyle = 'h-2 w-2';
